import { hasEnoughCoverage } from '@/engine/coverage';
import { nutrientMeta } from '@/engine/nutrients';
import type { NutrientKey } from '@/engine/types';
import { nutrientValue, percent } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Props {
  nutrientKey: NutrientKey;
  value: number | null | undefined;
  target?: number;
  /** Tope que no conviene superar (sodio, saturadas). */
  limit?: number;
  coverage: number;
}

/**
 * Una fila del panel del día.
 *
 * Tres estados bien distintos, y ninguno se comunica solo con color:
 *   - datos insuficientes -> se dice con palabras, y NO se pinta como déficit
 *   - exceso sobre el tope -> se marca y se nombra
 *   - normal -> barra con el % del objetivo
 */
export function NutrientRow({ nutrientKey, value, target, limit, coverage }: Props) {
  const meta = nutrientMeta(nutrientKey);
  const enough = hasEnoughCoverage(coverage);
  const over = limit != null && value != null && value > limit;
  const ratio = target && target > 0 && value != null ? value / target : null;

  return (
    <li className="flex flex-col gap-1 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm">{meta.label}</span>
        <span className={cn('text-sm tabular', over && 'font-semibold text-[var(--color-danger)]')}>
          {nutrientValue(nutrientKey, value)}
          {target ? (
            <span className="ml-1 text-xs text-[var(--color-text-muted)]">
              / {nutrientValue(nutrientKey, target)}
            </span>
          ) : null}
        </span>
      </div>

      {!enough ? (
        <p className="text-xs text-[var(--color-text-muted)]">
          Datos insuficientes: solo se conoce este nutriente en el {percent(coverage)} de lo que has
          comido hoy.
        </p>
      ) : ratio != null ? (
        <div className="flex items-center gap-2">
          <div
            role="progressbar"
            aria-valuenow={Math.round(ratio * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${meta.label}: ${Math.round(ratio * 100)} % del objetivo`}
            className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--color-surface-2)]"
          >
            <div
              className={cn(
                'h-full rounded-full transition-[width]',
                over ? 'bg-[var(--color-danger)]' : 'bg-[var(--color-brand)]',
              )}
              style={{ width: `${Math.min(ratio * 100, 100)}%` }}
            />
          </div>
          <span className="w-12 text-right text-xs text-[var(--color-text-muted)] tabular">
            {percent(ratio)}
          </span>
        </div>
      ) : null}

      {over ? (
        <p className="text-xs text-[var(--color-danger)]">
          Por encima del límite recomendado ({nutrientValue(nutrientKey, limit)}).
        </p>
      ) : null}
    </li>
  );
}
